/**
 * Trace Dosing Batches - Main page with templates and history
 *
 * Displays user's saved dosing templates (reusable recipes) and
 * dosing history (past batches). Users can click templates to start
 * new batches or create from scratch.
 */
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  Table,
  ScrollArea,
  Loader,
  Center,
  Alert,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Card,
  Button,
  Anchor,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconPlus,
  IconPencil,
  IconCopy,
  IconFlask,
  IconTrash,
  IconNotes,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDosingTemplates, useDosingBatches, useDeleteTemplate } from '@/hooks/useTraceDosing';
import { useTank } from '@/contexts/TankContext';
import type { DosingBatch } from '@/types/traceDosing';

const BADGE_COLORS = ['blue', 'grape', 'green', 'orange', 'cyan', 'teal'];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Helper to get all elements from all containers in a batch
function getAllElements(batch: DosingBatch): Array<{ element_name: string; ml_per_day: number }> {
  return batch.containers?.flatMap(c => c.elements) || [];
}

// Helper to get total ml/day from all containers
function getTotalMlPerDay(batch: DosingBatch): number {
  return batch.containers?.reduce((sum, c) => sum + c.ml_per_day, 0) || 0;
}

export default function TraceDosingPage() {
  const navigate = useNavigate();
  const { selectedTank } = useTank();
  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = useDosingTemplates();
  const { data: batches = [], isLoading: batchesLoading, error: batchesError } = useDosingBatches();
  const deleteTemplateMutation = useDeleteTemplate();

  const handleUseTemplate = (template: DosingBatch) => {
    // Navigate to calculator with template data in state
    navigate('/trace-dosing/new', { state: { template } });
  };

  const handleStartFresh = () => {
    navigate('/trace-dosing/new');
  };

  const handleEditBatch = (batch: DosingBatch) => {
    navigate(`/trace-dosing/${batch.id}/edit`, { state: { batch } });
  };

  const handleCopyBatch = (batch: DosingBatch) => {
    // Navigate to calculator with batch data as template
    navigate('/trace-dosing/new', { state: { copyFrom: batch } });
  };

  const handleDeleteTemplate = async (template: DosingBatch, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (!confirm(`Remove template "${template.name}"? The batch history will be kept.`)) {
      return;
    }

    if (!selectedTank?.id) {
      notifications.show({
        title: 'Error',
        message: 'No tank selected',
        color: 'red',
      });
      return;
    }

    try {
      await deleteTemplateMutation.mutateAsync({ batchId: template.id, tankId: selectedTank.id });
      notifications.show({
        title: 'Template removed',
        message: 'Template removed (batch kept in history)',
        color: 'blue',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to remove template',
        color: 'red',
      });
    }
  };

  // Show loading if queries are loading
  const isDataLoading = templatesLoading || batchesLoading;

  if (isDataLoading) {
    return (
      <Container size="xl" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Only show empty state when we've confirmed there's no data (not just waiting for it)
  const isEmptyState = templates.length === 0 && batches.length === 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with title and action button */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1}>Trace Dosing Calculator</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Calculate trace element volumes for batch preparation
            </Text>
          </div>
          {!isEmptyState && (
            <Button
              size="md"
              leftSection={<IconPlus size={18} />}
              onClick={handleStartFresh}
            >
              New Batch
            </Button>
          )}
        </Group>

        {/* Errors */}
        {(templatesError || batchesError) && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load data. Please try again.
          </Alert>
        )}

        {/* Empty State for new users */}
        {isEmptyState && (
          <Paper p="xl" withBorder radius="md">
            <Stack align="center" gap="md">
              <ThemeIcon size={64} radius="md" variant="light" color="blue">
                <IconFlask size={40} />
              </ThemeIcon>

              <Stack align="center" gap="xs">
                <Text size="xl" fw={600}>Ditch the Dosing Spreadsheet</Text>
                <Text size="sm" c="dimmed" ta="center" maw={600}>
                  Input your tank size and daily dosing targets. We'll calculate the exact milliliters
                  for each trace element and the dilution water needed. Save successful batches
                  as templates you can adjust and reuse.
                </Text>
              </Stack>

              <Button
                size="lg"
                leftSection={<IconPlus size={20} />}
                onClick={handleStartFresh}
              >
                Create Your First Batch
              </Button>

              <Text size="xs" c="dimmed" ta="center" maw={400}>
                Save batches as templates - one click to recalculate next time.
              </Text>
            </Stack>
          </Paper>
        )}

        {/* Templates Section - only show when templates exist */}
        {templates.length > 0 && (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between">
                <Text fw={600} size="lg">Your Templates</Text>
                <Text size="sm" c="dimmed">Click to reuse</Text>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
                {templates.map((template) => {
                  const elements = getAllElements(template);
                  const mlPerDay = getTotalMlPerDay(template);
                  return (
                    <Card
                      key={template.id}
                      padding="md"
                      withBorder
                      style={{ cursor: 'pointer', position: 'relative' }}
                      onClick={() => handleUseTemplate(template)}
                    >
                      <Tooltip label="Remove template">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          style={{ position: 'absolute', top: 8, right: 8 }}
                          onClick={(e) => handleDeleteTemplate(template, e)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Stack gap="xs">
                        <Text fw={600}>{template.name}</Text>
                        <Text size="sm" c="dimmed">
                          {template.containers?.length || 0} containers | {elements.length} elements | {template.days} days | {mlPerDay} ml/day
                        </Text>
                        <Group gap={4} wrap="wrap">
                          {elements.slice(0, 3).map((el, i) => (
                            <Badge key={el.element_name} variant="light" color={BADGE_COLORS[i % BADGE_COLORS.length]}>
                              {el.element_name}
                            </Badge>
                          ))}
                          {elements.length > 3 && (
                            <Badge variant="light" color="gray">+{elements.length - 3}</Badge>
                          )}
                        </Group>
                      </Stack>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </Stack>
          </Paper>
        )}

        {/* Batch History Section - only show when not in empty state */}
        {!isEmptyState && (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Text fw={600} size="lg">Batch History</Text>

              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Batch Name</Table.Th>
                      <Table.Th>Start Date</Table.Th>
                      <Table.Th>End Date</Table.Th>
                      <Table.Th>Containers</Table.Th>
                      <Table.Th>Elements</Table.Th>
                      <Table.Th>Volume</Table.Th>
                      <Table.Th>Notes</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {batches.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={8}>
                          <Center py="xl">
                            <Text c="dimmed">
                              No batches recorded yet.{' '}
                              <Anchor component="button" onClick={handleStartFresh}>
                                Create your first batch
                              </Anchor>
                            </Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      batches.map((batch) => {
                        const elements = getAllElements(batch);
                        return (
                          <Table.Tr key={batch.id}>
                            <Table.Td>
                              <Text fw={600}>{batch.name}</Text>
                            </Table.Td>
                            <Table.Td>{formatDate(batch.start_date)}</Table.Td>
                            <Table.Td>{formatDate(batch.end_date)}</Table.Td>
                            <Table.Td>{batch.containers?.length || 0}</Table.Td>
                            <Table.Td>
                              <Group gap={4} wrap="wrap">
                                {elements.slice(0, 3).map((el, i) => (
                                  <Badge key={el.element_name} variant="light" color={BADGE_COLORS[i % BADGE_COLORS.length]}>
                                    {el.element_name}
                                  </Badge>
                                ))}
                                {elements.length > 3 && (
                                  <Badge variant="light" color="gray">+{elements.length - 3}</Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td>{batch.total_volume_ml} ml</Table.Td>
                            <Table.Td>
                              {batch.notes ? (
                                <Tooltip label={batch.notes} multiline w={300}>
                                  <IconNotes size={16} style={{ color: 'var(--mantine-color-dimmed)', cursor: 'help' }} />
                                </Tooltip>
                              ) : (
                                <Text c="dimmed" size="xs">—</Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Group gap={4}>
                                <Tooltip label="Edit batch">
                                  <ActionIcon variant="subtle" onClick={() => handleEditBatch(batch)}>
                                    <IconPencil size={16} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Copy as new batch">
                                  <ActionIcon variant="subtle" onClick={() => handleCopyBatch(batch)}>
                                    <IconCopy size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
